import { useEffect, useMemo } from 'react';
import { Layer, type MapMouseEvent, Source, useMap } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { circle } from '@turf/turf';
import { flatMap, groupBy } from 'lodash';
import { useTheme } from 'styled-components';

import { SHAPE_FILL_OPACITY } from '../../../constants';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import {
  createTorusRingFeature,
  getZoneColor,
  getZoneFillColor,
  getZoneFillOpacity
} from '../../../MultiFlightMap.helpers';

import type { Feature, FeatureCollection, Polygon } from 'geojson';
import type { LatLng } from 'sonata-static-storage/lib/schemas/zone/zone.schema';
import type { ModifiedZone } from '../../MapView/map.types';

type Props = {
  mapId: string;
};

export function CircleShapeLayer({ mapId }: Props) {
  const { circleZones, editedCircleId, setEditedCircleId, setList, setSelectedItemId, torusZones } =
    useMultiFlightMap();
  const { uiColors }: any = useTheme();
  const { [mapId]: map } = useMap();

  useEffect(() => {
    const mapInstance = map?.getMap();

    if (!mapInstance) {
      return;
    }

    const handleClick = (e: MapMouseEvent) => {
      const feature = e.features?.[0];
      const id = feature?.properties?._id;
      setEditedCircleId(id);
      setSelectedItemId(id.toString() || null);
      setList('locations');
    };

    mapInstance.on('click', ['circle-fill', 'torus-fill'], handleClick);

    return () => {
      mapInstance.off('click', ['circle-fill', 'torus-fill'], handleClick);
    };
  }, [map, setEditedCircleId]);

  const circleFeatures = useMemo<Feature<Polygon>[]>(() => {
    return (
      circleZones
        .filter(shape => shape._id !== editedCircleId)
        .filter(
          (circleZone): circleZone is ModifiedZone & { latlng: LatLng } =>
            circleZone.type === 'circle' &&
            !!circleZone.latlng &&
            typeof circleZone.radius === 'number' &&
            !!circleZone.radius
        )
        .map(circleZone =>
          circle([circleZone.latlng.lng, circleZone.latlng.lat], circleZone.radius! / 1000, {
            properties: {
              _id: circleZone._id,
              color: getZoneColor(uiColors, circleZone),
              fillColor: getZoneFillColor(uiColors, circleZone),
              fillOpacity: getZoneFillOpacity(circleZone, SHAPE_FILL_OPACITY)
            },
            steps: 64,
            units: 'kilometers'
          })
        )
        // @ts-expect-error
        .filter((f): f is Feature<Polygon> => f.geometry?.type === 'Polygon')
    );
  }, [circleZones, editedCircleId, uiColors]);

  const featureCollection: FeatureCollection<Polygon> = {
    features: circleFeatures,
    type: 'FeatureCollection'
  };

  const torusFeatures = useMemo<Feature<Polygon>[]>(() => {
    const editedTorus = torusZones.find(zone => zone._id === editedCircleId);
    const torusZonesGrouped = groupBy(
      torusZones.filter(
        zone =>
          zone.type === 'torus' &&
          zone._id !== editedCircleId &&
          zone.parentTorus !== editedTorus?.parentTorus &&
          zone.latlng
      ),
      'parentTorus'
    );

    return flatMap(Object.values(torusZonesGrouped), zonesArray => {
      const center: [number, number] = [zonesArray[0].latlng!.lng, zonesArray[0].latlng!.lat];
      const configs = zonesArray.filter(z => typeof z.radius === 'number').sort((a, b) => b.radius! - a.radius!);

      if (configs.length === 1) {
        const cfg = configs[0];
        return [
          circle(center, cfg.radius! / 1000, {
            properties: {
              _id: cfg._id,
              color: getZoneColor(uiColors, cfg),
              fillColor: getZoneFillColor(uiColors, cfg),
              fillOpacity: getZoneFillOpacity(cfg, 0.3)
            },
            steps: 64,
            units: 'kilometers'
          })
        ];
      }

      return configs.map((cfg, idx) => {
        const outerRadius = cfg.radius!;
        const innerRadius = configs[idx + 1]?.radius ?? 0;
        if (innerRadius === 0) {
          return circle(center, outerRadius / 1000, {
            properties: {
              _id: cfg._id,
              color: getZoneColor(uiColors, cfg),
              fillColor: getZoneFillColor(uiColors, cfg),
              fillOpacity: getZoneFillOpacity(cfg, 0.3)
            },
            steps: 64,
            units: 'kilometers'
          });
        }

        return createTorusRingFeature(center, outerRadius, innerRadius, {
          _id: cfg._id,
          color: getZoneColor(uiColors, cfg),
          fillColor: getZoneFillColor(uiColors, cfg),
          fillOpacity: getZoneFillOpacity(cfg, 0.3)
        });
      });
    }).filter((f): f is Feature<Polygon> => !!f && !!f.geometry && f.geometry.type === 'Polygon');
  }, [editedCircleId, torusZones]);

  const torusFeatureCollection: FeatureCollection<Polygon> = {
    features: torusFeatures,
    type: 'FeatureCollection'
  };

  return (
    <>
      <Source data={featureCollection} id="circle-zones" type="geojson">
        <Layer
          id="circle-fill"
          paint={{
            'fill-color': ['get', 'fillColor'],
            'fill-opacity': ['get', 'fillOpacity']
          }}
          type="fill"
        />
        <Layer
          id="circle-outline"
          paint={{
            'line-color': ['get', 'color'],
            'line-width': 2
          }}
          type="line"
        />
      </Source>
      <Source data={torusFeatureCollection} id="torus-zones" type="geojson">
        <Layer
          id="torus-fill"
          paint={{
            'fill-color': ['get', 'fillColor'],
            'fill-opacity': ['get', 'fillOpacity']
          }}
          type="fill"
        />
        <Layer
          id="torus-outline"
          paint={{
            'line-color': ['get', 'color'],
            'line-width': 2
          }}
          type="line"
        />
      </Source>
    </>
  );
}
